import React, { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import axios from "axios";
import { 
  Calculator, 
  TrendingUp,
  IndianRupee,
  Calendar,
  Search,
  ArrowRight,
  RefreshCw,
  PiggyBank,
  Wallet,
  LineChart
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const FundSearchInput = ({ value, onChange, onSelect, selectedFund }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);

  const searchFunds = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/funds/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.funds || []);
      setShowDropdown(true);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchFunds(value), 300);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search for a mutual fund..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          className="pl-10"
          data-testid="fund-search-input"
        />
        {searching && (
          <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>
      
      {showDropdown && searchResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {searchResults.map((fund) => (
            <button
              key={fund.schemeCode}
              className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
              onClick={() => {
                onSelect(fund);
                setShowDropdown(false);
              }}
            >
              <div className="font-medium text-sm">{fund.schemeName}</div>
              <div className="text-xs text-muted-foreground">Code: {fund.schemeCode}</div>
            </button>
          ))}
        </div>
      )}
      
      {selectedFund && (
        <div className="mt-2 p-3 bg-primary/10 rounded-md border border-primary/20">
          <div className="font-medium text-sm text-primary">{selectedFund.schemeName}</div>
          <div className="text-xs text-muted-foreground">Scheme Code: {selectedFund.schemeCode}</div>
        </div>
      )}
    </div>
  );
};

export const RealReturnsCalculatorPage = () => {
  const [activeTab, setActiveTab] = useState("sip");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFund, setSelectedFund] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // SIP inputs
  const [sipAmount, setSipAmount] = useState(10000);
  const [sipYears, setSipYears] = useState(5);
  
  // Lumpsum inputs
  const [lumpsumAmount, setLumpsumAmount] = useState(100000);
  const [lumpsumYears, setLumpsumYears] = useState(5);

  const calculateReturns = async () => {
    if (!selectedFund) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const today = new Date();
      const years = activeTab === "sip" ? sipYears : lumpsumYears;
      const startDate = new Date(today);
      startDate.setFullYear(startDate.getFullYear() - years);
      
      const params = new URLSearchParams({
        scheme_code: selectedFund.schemeCode,
        investment_type: activeTab,
        amount: activeTab === "sip" ? sipAmount : lumpsumAmount,
        start_date: `${startDate.getDate().toString().padStart(2, '0')}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getFullYear()}`,
        end_date: `${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}`
      });

      const response = await axios.get(`${API}/funds/calculate-returns?${params}`);
      setResult(response.data);
    } catch (error) {
      console.error("Error calculating returns:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Generate chart data from SIP details
  const getChartData = () => {
    if (!result?.sip_details) return [];
    return result.sip_details.map(d => ({
      date: d.date,
      invested: d.cumulative_invested,
      value: d.cumulative_units * result.current_nav
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="real-returns-calculator-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Calculator className="w-8 h-8 text-primary" />
            Returns Calculator
          </h1>
          <p className="text-muted-foreground mt-1">
            Calculate actual returns using real NAV data from AMFI
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calculator Panel */}
          <Card className="bg-card/50 border-border/50 lg:col-span-1">
            <CardHeader>
              <CardTitle>Calculate Returns</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Fund Search */}
              <div className="space-y-2">
                <Label>Select Fund</Label>
                <FundSearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSelect={(fund) => {
                    setSelectedFund(fund);
                    setSearchQuery(fund.schemeName);
                  }}
                  selectedFund={selectedFund}
                />
              </div>

              {/* Investment Type Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="sip" className="flex items-center gap-1">
                    <PiggyBank className="w-4 h-4" /> SIP
                  </TabsTrigger>
                  <TabsTrigger value="lumpsum" className="flex items-center gap-1">
                    <Wallet className="w-4 h-4" /> Lumpsum
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="sip" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Monthly SIP Amount (₹)</Label>
                    <Input
                      type="number"
                      value={sipAmount}
                      onChange={(e) => setSipAmount(Number(e.target.value))}
                      min={500}
                      step={500}
                      data-testid="sip-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Investment Period (Years)</Label>
                    <div className="flex gap-2">
                      {[1, 3, 5, 7, 10].map(y => (
                        <Button
                          key={y}
                          variant={sipYears === y ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSipYears(y)}
                        >
                          {y}Y
                        </Button>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="lumpsum" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Investment Amount (₹)</Label>
                    <Input
                      type="number"
                      value={lumpsumAmount}
                      onChange={(e) => setLumpsumAmount(Number(e.target.value))}
                      min={1000}
                      step={10000}
                      data-testid="lumpsum-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Investment Period (Years)</Label>
                    <div className="flex gap-2">
                      {[1, 3, 5, 7, 10].map(y => (
                        <Button
                          key={y}
                          variant={lumpsumYears === y ? "default" : "outline"}
                          size="sm"
                          onClick={() => setLumpsumYears(y)}
                        >
                          {y}Y
                        </Button>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <Button 
                className="w-full" 
                onClick={calculateReturns}
                disabled={!selectedFund || loading}
                data-testid="calculate-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="w-4 h-4 mr-2" />
                )}
                Calculate Returns
              </Button>
            </CardContent>
          </Card>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            {result ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-card/50 border-border/50">
                    <CardContent className="p-4 text-center">
                      <Wallet className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                      <div className="text-2xl font-bold text-foreground">
                        {formatCurrency(result.total_invested)}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Invested</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card/50 border-border/50">
                    <CardContent className="p-4 text-center">
                      <IndianRupee className="w-6 h-6 text-primary mx-auto mb-2" />
                      <div className="text-2xl font-bold text-primary">
                        {formatCurrency(result.current_value)}
                      </div>
                      <div className="text-xs text-muted-foreground">Current Value</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card/50 border-border/50">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
                      <div className={`text-2xl font-bold ${result.absolute_return >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrency(result.absolute_return)}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Gain/Loss</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card/50 border-border/50">
                    <CardContent className="p-4 text-center">
                      <LineChart className="w-6 h-6 text-primary mx-auto mb-2" />
                      <div className={`text-2xl font-bold ${(result.cagr || result.xirr_approx) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {(result.cagr || result.xirr_approx)?.toFixed(2)}%
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {result.investment_type === "SIP" ? "XIRR (Approx)" : "CAGR"}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Fund Details */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg">{result.scheme_name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Investment Type</span>
                        <div className="font-medium">{result.investment_type}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Period</span>
                        <div className="font-medium">{result.start_date} - {result.end_date}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">
                          {result.investment_type === "SIP" ? "Months" : "Years"}
                        </span>
                        <div className="font-medium">
                          {result.months || result.years}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Absolute Return</span>
                        <div className={`font-medium ${result.absolute_return_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {result.absolute_return_pct >= 0 ? '+' : ''}{result.absolute_return_pct?.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Chart */}
                {result.sip_details && (
                  <Card className="bg-card/50 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Investment Growth</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={getChartData()}>
                            <defs>
                              <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#64748b" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#64748b" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                            <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                              labelStyle={{ color: '#F8FAFC' }}
                              formatter={(value) => [formatCurrency(value), '']}
                            />
                            <Area type="monotone" dataKey="invested" stroke="#64748b" fillOpacity={1} fill="url(#colorInvested)" name="Invested" />
                            <Area type="monotone" dataKey="value" stroke="#F59E0B" fillOpacity={1} fill="url(#colorValue)" name="Value" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-12 text-center">
                  <Calculator className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold">Calculate Your Returns</h3>
                  <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                    Select a mutual fund and enter your investment details to see actual historical returns based on real NAV data.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Calendar className="w-8 h-8 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">How it works</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This calculator uses actual historical NAV data from AMFI to compute real returns. 
                  For SIP, it simulates monthly investments at actual NAV on each date. 
                  Past performance is not indicative of future returns.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
